import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import config from '../config';
import RuleScreen from '../shared/RuleScreen';
import FilterBar from '../shared/FilterBar';
import StatTile from '../shared/StatTile';
import DataTable from '../shared/DataTable';
import Pagination, { normalizePagination } from '../shared/Pagination';
import DetailDrawer from '../shared/DetailDrawer';
import { buildParams } from '../shared/apiParams';

const API_URL = config.apiUrl;
const EMPTY = { blocks: [], gramPanchayats: [], villages: [], enumerators: [], startDate: '', endDate: '', matchStatus: 'all' };

const TABS = [
  { key: 'enumerator', label: 'By Enumerator', col: 'Enumerator ID' },
  { key: 'block', label: 'By Block', col: 'Block Name' },
  { key: 'gp', label: 'By Gram Panchayat', col: 'Gram Panchayat' },
  { key: 'village', label: 'By Village', col: 'Village Name' },
];

const MATCH_OPTIONS = [
  { value: 'all', label: 'All Records' },
  { value: 'exact', label: 'Exact Match (≥80%)' },
  { value: 'partial', label: 'Partial Match (50–79%)' },
  { value: 'low', label: 'Low Match (25–49%)' },
  { value: 'notexists', label: 'Not Exists (<25%)' },
];

const pctCell = (count, total) => (
  <>
    {count || 0}
    {total > 0 ? <span style={{ fontSize: '0.72rem', color: 'var(--muted)', marginLeft: 4 }}>({Math.round(((count || 0) / total) * 100)}%)</span> : null}
  </>
);

export default function Member({ rule }) {
  const [activeTab, setActiveTab] = useState('enumerator');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(normalizePagination({}));
  const [applied, setApplied] = useState(EMPTY);
  const [drawer, setDrawer] = useState({ open: false, row: null, category: 'all', records: [], loading: false });
  const [memberPopup, setMemberPopup] = useState({ open: false, loading: false, respondentName: '', members: [], matchDetails: null });

  const tab = TABS.find((t) => t.key === activeTab);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(applied, { extra: { page, limit: pageSize } });
      let url;
      if (activeTab === 'enumerator') {
        url = `${API_URL}/member-matching/enumerator`;
      } else {
        params.append('forceGroupBy', activeTab);
        url = `${API_URL}/member-matching/dynamic`;
      }
      const res = await axios.get(`${url}?${params}`);
      if (res.data?.success) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || null);
        setPagination(normalizePagination(res.data.pagination || {}));
      }
    } catch (err) {
      console.error('Error fetching member matching data:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab, applied, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const firstColValue = (item) => (activeTab === 'enumerator' ? item.enumerator_id : item.name);

  // Tiles: prefer backend summary, else sum current page.
  const sum = (k) => rows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
  const totalSurveys = summary?.totalSurveys ?? sum('totalSurveys');
  const exact = summary?.exactMatches ?? sum('exactMatches');
  const partial = summary?.partialMatches ?? sum('partialMatches');
  const notExists = summary?.notExists ?? sum('notExists');

  const tiles = (
    <>
      <StatTile label="Total Surveys" value={totalSurveys} icon={GroupsIcon} accent="var(--primary)" sub={summary ? undefined : 'Current page'} />
      <StatTile label="Exact Matches" value={exact} icon={CheckCircleIcon} accent="var(--success)" />
      <StatTile label="Partial Matches" value={partial} icon={WarningAmberIcon} accent="var(--warning)" />
      <StatTile label="Not Found" value={notExists} icon={ErrorIcon} accent="var(--danger)" />
    </>
  );

  const CATEGORY_LABEL = { all: 'All Records', exact: 'Exact (≥80%)', partial: 'Partial (50–79%)', low: 'Low (25–49%)', notexists: 'Not Exists (<25%)' };

  const openDrilldown = async (row, category = 'all') => {
    setDrawer({ open: true, row, category, records: [], loading: true });
    try {
      const params = buildParams({}, {
        extra: { groupBy: activeTab, groupValue: firstColValue(row), category, limit: 100 },
      });
      const res = await axios.get(`${API_URL}/member-details-records?${params}`);
      const records = res.data?.data || [];
      setDrawer({ open: true, row, category, records, loading: false });
    } catch (err) {
      console.error('Error fetching member detail records:', err);
      setDrawer({ open: true, row, category, records: [], loading: false });
    }
  };

  const countCell = (row, count, category) => (
    <span
      className="clickable-name"
      onClick={(e) => { e.stopPropagation(); openDrilldown(row, category); }}
    >
      {pctCell(count, row.totalSurveys)}
    </span>
  );

  const columns = [
    { key: 'first', label: tab.col, render: (r) => <span className="cell-strong">{firstColValue(r) || 'N/A'}</span> },
    { key: 'totalSurveys', label: 'Total', align: 'center', render: (r) => <span className="cell-strong">{r.totalSurveys || 0}</span> },
    { key: 'exactMatches', label: 'Exact (≥80%)', align: 'center', render: (r) => countCell(r, r.exactMatches, 'exact') },
    { key: 'partialMatches', label: 'Partial (50–79%)', align: 'center', render: (r) => countCell(r, r.partialMatches, 'partial') },
    { key: 'lowMatches', label: 'Low (25–49%)', align: 'center', render: (r) => countCell(r, r.lowMatches, 'low') },
    { key: 'notExists', label: 'Not Exists (<25%)', align: 'center', render: (r) => countCell(r, r.notExists, 'notexists') },
    {
      key: 'rate', label: 'Match Rate', align: 'center',
      render: (r) => {
        const t = r.totalSurveys || 0;
        const rate = t > 0 ? Math.round((((r.exactMatches || 0) + (r.partialMatches || 0)) / t) * 100) : 0;
        const cls = rate >= 80 ? 'badge-success' : rate >= 50 ? 'badge-warning' : 'badge-danger';
        return <span className={`badge ${cls}`}>{rate}%</span>;
      },
    },
  ];

  const openMemberPopup = async (record) => {
    const survey = record.survey || {};
    setMemberPopup({ open: true, loading: true, respondentName: survey.respondent_name, members: [], matchDetails: null });
    try {
      const params = buildParams({}, {
        extra: {
          householdId: survey.household_id,
          submissionUuid: survey._uuid,
          respondentName: survey.respondent_name,
          totalHouseholdMember: survey.total_household_member,
        },
      });
      const res = await axios.get(`${API_URL}/member-details?${params}`);
      setMemberPopup({
        open: true,
        loading: false,
        respondentName: survey.respondent_name,
        members: res.data?.members || [],
        matchDetails: res.data?.matchDetails || null,
      });
    } catch (err) {
      console.error('Error fetching member details:', err);
      setMemberPopup((p) => ({ ...p, loading: false }));
    }
  };

  const tierFor = (similarity) => {
    const s = similarity ?? 0;
    if (s >= 80) return 'exact';
    if (s >= 50) return 'partial';
    if (s >= 25) return 'low';
    return 'notexists';
  };
  const tierBadgeClass = { exact: 'badge-success', partial: 'badge-warning', low: 'badge-warning', notexists: 'badge-danger' };

  const memberPopupColumns = [
    { key: 'member_name', label: 'Member Name', render: (m) => <span className="cell-strong">{m.member_name || '—'}</span> },
    { key: 'member_relation_hoh', label: 'Relation', render: (m) => m.member_relation_hoh || '—' },
    { key: 'similarity', label: 'Name Match %', align: 'center', render: (m) => `${m.similarity ?? 0}%` },
    {
      key: 'tier', label: 'Tier', align: 'center',
      render: (m) => {
        const t = tierFor(m.similarity);
        return <span className={`badge ${tierBadgeClass[t]}`}>{CATEGORY_LABEL[t]}</span>;
      },
    },
  ];

  const drawerRecordColumns = [
    {
      key: 'respondent_name', label: 'Respondent Name',
      render: (r) => (
        <span className="clickable-name" onClick={(e) => { e.stopPropagation(); openMemberPopup(r); }}>
          {r.survey?.respondent_name || '—'}
        </span>
      ),
    },
    { key: 'member_name', label: 'Matched Member Name', render: (r) => r.match?.matchedMemberName || '—' },
    { key: 'score', label: 'Match %', align: 'center', render: (r) => `${r.match?.score ?? 0}%` },
    {
      key: 'status', label: 'Match / Not Match', align: 'center',
      render: (r) => (
        <span className={`badge ${r.match?.memberExists ? 'badge-success' : 'badge-danger'}`}>
          {r.match?.memberExists ? `Match (${CATEGORY_LABEL[r.match?.status] || r.match?.status})` : 'Not Match'}
        </span>
      ),
    },
    { key: 'block', label: 'Block', render: (r) => r.survey?.block || '—' },
    { key: 'village_name', label: 'Village', render: (r) => r.survey?.village_name || '—' },
  ];

  const filterBar = (
    <FilterBar
      onApply={(v) => { setApplied(v); setPage(1); }}
      onClear={() => { setApplied(EMPTY); setPage(1); }}
      loading={loading}
      extraFilters={[
        { name: 'matchStatus', label: 'Match Status', options: MATCH_OPTIONS, defaultValue: 'all', isClearable: false },
      ]}
    />
  );

  return (
    <RuleScreen
      title={rule?.title || 'Member Matching'}
      subtitle="Fuzzy match of respondent name against the household member roster (precomputed)."
      icon={PersonSearchIcon}
      backTo={rule ? `/category/household` : undefined}
      backLabel="Back to Household"
      tiles={tiles}
      filterBar={
        <>
          <div className="tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`tab-btn${activeTab === t.key ? ' active' : ''}`}
                onClick={() => { if (t.key !== activeTab) { setActiveTab(t.key); setPage(1); } }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {filterBar}
        </>
      }
    >
      <div className="card">
        <h3 className="card-title">Statistics by {tab.label.replace('By ', '')}</h3>
        <Pagination
          pagination={pagination}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          unitLabel="groups"
          shown={rows.length}
        />
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage='No data found. Adjust filters and click "Apply Filters".'
          onRowClick={(r) => openDrilldown(r, 'all')}
          getRowKey={(r, i) => `${firstColValue(r)}-${i}`}
        />
      </div>

      <DetailDrawer
        open={drawer.open}
        onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        title={drawer.row ? `${tab.col}: ${firstColValue(drawer.row)} — ${CATEGORY_LABEL[drawer.category] || 'All Records'}` : ''}
        sections={
          drawer.row
            ? [
                {
                  title: 'Match Breakdown',
                  items: [
                    { label: 'Total Surveys', value: drawer.row.totalSurveys || 0 },
                    { label: 'Exact', value: drawer.row.exactMatches || 0 },
                    { label: 'Partial', value: drawer.row.partialMatches || 0 },
                    { label: 'Low', value: drawer.row.lowMatches || 0 },
                    { label: 'Not Exists', value: drawer.row.notExists || 0 },
                  ],
                },
              ]
            : []
        }
      >
        <div className="detail-section">
          <h4>Flagged Records</h4>
          <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginTop: '-0.4rem' }}>
            Click a respondent's name to see their household members and how each name matches.
          </p>
          <DataTable
            columns={drawerRecordColumns}
            rows={drawer.records}
            loading={drawer.loading}
            emptyMessage="No detail records available."
            getRowKey={(r, i) => r._uuid || i}
          />
        </div>
      </DetailDrawer>

      <DetailDrawer
        open={memberPopup.open}
        onClose={() => setMemberPopup((p) => ({ ...p, open: false }))}
        title={`Household Members: ${memberPopup.respondentName || ''}`}
        sections={
          memberPopup.matchDetails
            ? [
                {
                  title: 'Match Summary',
                  items: [
                    { label: 'Household Members', value: memberPopup.matchDetails.householdMemberCount ?? memberPopup.members.length },
                    { label: 'Best Matched Member', value: memberPopup.matchDetails.matchedMemberName || '—' },
                    { label: 'Matched Relation', value: memberPopup.matchDetails.matchedMemberRelation || '—' },
                    { label: 'Match Score', value: `${memberPopup.matchDetails.nameMatchScore ?? 0}%` },
                    { label: 'Status', value: CATEGORY_LABEL[memberPopup.matchDetails.status] || memberPopup.matchDetails.status || '—' },
                  ],
                },
              ]
            : []
        }
      >
        <div className="detail-section">
          <h4>All Household Members ({memberPopup.members.length})</h4>
          <DataTable
            columns={memberPopupColumns}
            rows={memberPopup.members}
            loading={memberPopup.loading}
            emptyMessage="No household members found for this respondent."
            getRowKey={(m, i) => m.member_unique_id || i}
          />
        </div>
      </DetailDrawer>
    </RuleScreen>
  );
}
